import { normalizeShipmentContacts } from "../../shipment-contact.schema";
import { shippingTextSchema } from "@/lib/shipping-text";
import { useForm, useFieldArray, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button, Chip, Input, Switch, Textarea } from "@heroui/react";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { AddressBookSelector } from "@/features/address-book/components/AddressBookSelector";
import type { CustomerContact } from "@/features/address-book/types";
import { UnitSelector } from "@/components/unit-selector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PackagingSelector } from "@/components/packaging-selector";
import { Plus, Trash2, Package, User, MapPin } from "lucide-react";
import { useState, useCallback, useRef, useEffect } from "react";
import type { OrderDraft } from "../types";
import { PillToggle } from "../pill-toggle";
import { useTranslation } from "react-i18next";
import {
  countryCodeForValue,
  DEFAULT_COUNTRY_CODE,
  isoCountryCodeSchema,
} from "@/lib/countries";

interface ShipmentDetailsStepProps {
  data: OrderDraft;
  onNext: (data: Partial<OrderDraft>) => void;
  formId?: string;
  hideNavigation?: boolean;
  editParty?: "sender" | "recipient";
}

const addressSchema = z.object({
  street1: shippingTextSchema().min(1),
  street2: shippingTextSchema().optional(),
  city: shippingTextSchema().min(1),
  state: z.string().min(1),
  postalCode: z.string().min(1),
  country: isoCountryCodeSchema.default(DEFAULT_COUNTRY_CODE),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

export const packageSchema = z.object({
  weight: z.number().min(0.1),
  weightUnit: z.enum(["kg", "lb"]).default("kg"),
  length: z.number().min(0.1),
  width: z.number().min(0.1),
  height: z.number().min(0.1),
  dimensionUnit: z.enum(["cm", "in"]).default("cm"),
  packageKind: z.enum(["PARCEL", "THICK_ENVELOPE"]).optional(),
  description: z.string().optional(),
  isFragile: z.boolean().default(false),
  requiresCooling: z.boolean().default(false),
  declaredValueCents: z.number().optional(),
});

export type PackageFormData = z.infer<typeof packageSchema>;

export const shipmentSchema = z
  .object({
    senderName: shippingTextSchema().trim().min(1),
    senderEmail: z
      .string()
      .trim()
      .pipe(z.string().email().or(z.literal("")))
      .optional(),
    senderPhone: z.string().default(""),
    shipFrom: addressSchema,

    recipientName: shippingTextSchema().trim().min(1),
    recipientEmail: z
      .string()
      .trim()
      .pipe(z.string().email().or(z.literal("")))
      .optional(),
    recipientPhone: z.string().default(""),
    shipTo: addressSchema,

    packages: z.array(packageSchema).min(1),

    pickupNotes: z.string().optional(),
    deliveryNotes: z.string().optional(),
  })
  .transform(normalizeShipmentContacts);

export type ShipmentFormData = z.infer<typeof shipmentSchema>;

function AddressFieldErrors({
  errors,
}: {
  errors?: FieldErrors<
    Pick<z.infer<typeof addressSchema>, "street1" | "street2" | "city">
  >;
}) {
  const { t } = useTranslation("app");
  const labels = { street1: "line1", street2: "line2", city: "city" } as const;
  return (["street1", "street2", "city"] as const).map((field) => {
    const message = errors?.[field]?.message;
    return message ? (
      <p
        key={field}
        role="alert"
        className="text-sm font-medium text-destructive"
      >
        {t(`validation.shippingTextFields.${labels[field]}`)}: {message}
      </p>
    ) : null;
  });
}

export function ShipmentDetailsStep({
  data,
  onNext,
  formId,
  hideNavigation,
  editParty,
}: ShipmentDetailsStepProps) {
  // Opt this component out of React Compiler's auto-memoization because it
  // uses react-hook-form's `form.watch()` API which returns proxies the
  // compiler can't statically analyze.
  "use no memo";
  const { t } = useTranslation("app");
  const senderSection = useRef<HTMLElement>(null);
  const recipientSection = useRef<HTMLElement>(null);
  const [useSenderAddressBook, setUseSenderAddressBook] = useState(
    editParty !== "sender" &&
      (data.shipFromAddressBookEntryId != null ||
        !data.shipment?.shipFrom?.street1)
  );
  const [useRecipientAddressBook, setUseRecipientAddressBook] = useState(
    editParty !== "recipient" &&
      (data.shipToAddressBookEntryId != null || !data.shipment?.shipTo?.street1)
  );
  // Mobile-only: collapse the sender/recipient picker into a "Selected addr +
  // Change →" summary card once a value is picked (design section 5 · Mobile ·
  // Details pattern). Returning to the step with a populated draft starts
  // collapsed; an empty draft starts expanded so the picker is visible.
  const [senderExpanded, setSenderExpanded] = useState(
    editParty === "sender" || !data.shipment?.shipFrom?.street1
  );
  const [recipientExpanded, setRecipientExpanded] = useState(
    editParty === "recipient" || !data.shipment?.shipTo?.street1
  );
  // Phase 10a.5: capture which AddressBook entry the user picked. When set,
  // server reuses that entry's Address row so PickupBooking attach works.
  // Cleared on AddressBook→Manual toggle (the Manual form may diverge from
  // the entry's saved address).
  const [shipFromAddressBookEntryId, setShipFromAddressBookEntryId] = useState<
    string | undefined
  >(data.shipFromAddressBookEntryId);
  const [shipToAddressBookEntryId, setShipToAddressBookEntryId] = useState<
    string | undefined
  >(data.shipToAddressBookEntryId);
  const [selectedPackagingIds, setSelectedPackagingIds] = useState<
    Record<number, string | null>
  >({});

  const localizedAddressSchema = z.object({
    street1: shippingTextSchema(t("validation.shippingText")).min(
      1,
      t("createShipment.streetAddressRequired")
    ),
    street2: shippingTextSchema(t("validation.shippingText")).optional(),
    city: shippingTextSchema(t("validation.shippingText")).min(
      1,
      t("validation.cityRequired")
    ),
    state: z.string().min(1, t("validation.provinceRequired")),
    postalCode: z.string().min(1, t("validation.postalCodeRequired")),
    country: isoCountryCodeSchema.default(DEFAULT_COUNTRY_CODE),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
  });

  const localizedPackageSchema = z.object({
    weight: z.number().min(0.1, t("createShipment.weightRequired")),
    weightUnit: z.enum(["kg", "lb"]).default("kg"),
    length: z.number().min(0.1, t("createShipment.lengthRequired")),
    width: z.number().min(0.1, t("createShipment.widthRequired")),
    height: z.number().min(0.1, t("createShipment.heightRequired")),
    dimensionUnit: z.enum(["cm", "in"]).default("cm"),
    packageKind: z.enum(["PARCEL", "THICK_ENVELOPE"]).optional(),
    description: z.string().optional(),
    isFragile: z.boolean().default(false),
    requiresCooling: z.boolean().default(false),
    declaredValueCents: z.number().optional(),
  });

  const localizedShipmentSchema = z
    .object({
      senderName: shippingTextSchema(t("validation.shippingText"))
        .trim()
        .min(1, t("createShipment.senderNameRequired")),
      senderEmail: z
        .string()
        .trim()
        .pipe(
          z.string().email(t("createShipment.invalidEmail")).or(z.literal(""))
        )
        .optional(),
      senderPhone: z.string().default(""),
      shipFrom: localizedAddressSchema,
      recipientName: shippingTextSchema(t("validation.shippingText"))
        .trim()
        .min(1, t("createShipment.recipientNameRequired")),
      recipientEmail: z
        .string()
        .trim()
        .pipe(
          z.string().email(t("createShipment.invalidEmail")).or(z.literal(""))
        )
        .optional(),
      recipientPhone: z.string().default(""),
      shipTo: localizedAddressSchema,
      packages: z
        .array(localizedPackageSchema)
        .min(1, t("createShipment.packageRequired")),
      pickupNotes: z.string().optional(),
      deliveryNotes: z.string().optional(),
    })
    .transform((shipment, ctx) =>
      normalizeShipmentContacts(
        shipment,
        ctx,
        `${t("validation.phoneRequired")}. ${t("validation.phoneInvalid")}`
      )
    );

  const form = useForm({
    resolver: zodResolver(localizedShipmentSchema),
    defaultValues: data.shipment ?? {
      senderName: "",
      senderEmail: "",
      senderPhone: "",
      shipFrom: {
        street1: "",
        street2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "CA",
      },
      recipientName: "",
      recipientEmail: "",
      recipientPhone: "",
      shipTo: {
        street1: "",
        street2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "CA",
      },
      packages: [
        {
          weight: undefined as unknown as number,
          weightUnit: "kg" as const,
          length: undefined as unknown as number,
          width: undefined as unknown as number,
          height: undefined as unknown as number,
          dimensionUnit: "cm" as const,
          packageKind: "PARCEL" as const,
          description: "",
          isFragile: false,
          requiresCooling: false,
          declaredValueCents: 0,
        },
      ],
      pickupNotes: "",
      deliveryNotes: "",
    },
  });

  useEffect(() => {
    if (editParty)
      form.setFocus(editParty === "sender" ? "senderName" : "recipientName");
  }, [editParty, form]);

  const {
    fields: packageFields,
    append: appendPackage,
    remove: removePackage,
  } = useFieldArray({
    control: form.control,
    name: "packages",
  });

  const handleSubmit = form.handleSubmit(
    (values) => {
      // Phase 10a.5: forward AddressBook entry IDs alongside form values. When
      // present, server uses entry's Address row (enables PickupBooking attach).
      onNext({
        shipment: values,
        shipFromAddressBookEntryId: useSenderAddressBook
          ? shipFromAddressBookEntryId
          : undefined,
        shipToAddressBookEntryId: useRecipientAddressBook
          ? shipToAddressBookEntryId
          : undefined,
      });
    },
    (errors) => {
      const senderInvalid = !!(
        errors.senderName ||
        errors.senderPhone ||
        errors.senderEmail ||
        errors.shipFrom
      );
      const recipientInvalid = !!(
        errors.recipientName ||
        errors.recipientPhone ||
        errors.recipientEmail ||
        errors.shipTo
      );
      if (senderInvalid) setSenderExpanded(true);
      if (recipientInvalid) setRecipientExpanded(true);
      const section = senderInvalid
        ? senderSection
        : recipientInvalid
          ? recipientSection
          : null;
      const hasFocusableName =
        (errors.senderName && !useSenderAddressBook) ||
        (errors.recipientName && !useRecipientAddressBook);
      if (section && !hasFocusableName)
        requestAnimationFrame(() => section.current?.focus());
    }
  );

  const handleAddressBookSelect = useCallback(
    (type: "sender" | "recipient", contact: CustomerContact) => {
      if (type === "sender") {
        // Update sender fields
        form.setValue("senderName", contact.contactName);
        form.setValue("senderEmail", contact.contactEmail ?? "");
        form.setValue("senderPhone", contact.contactPhone ?? "");

        // Update shipFrom address
        form.setValue("shipFrom.street1", contact.addressLine1);
        form.setValue("shipFrom.street2", contact.addressLine2 ?? "");
        form.setValue("shipFrom.city", contact.addressCity);
        form.setValue("shipFrom.state", contact.addressProvince);
        form.setValue("shipFrom.postalCode", contact.addressPostalCode ?? "");
        form.setValue(
          "shipFrom.country",
          countryCodeForValue(contact.addressCountry) ?? ""
        );
        form.setValue("shipFrom.latitude", contact.location.lat);
        form.setValue("shipFrom.longitude", contact.location.lng);
        form.setValue("pickupNotes", contact.pickupInstructions ?? "");

        // Phase 10a.5: pin the entry ID — submit uses it to dedup PickupBooking
        setShipFromAddressBookEntryId(contact.id);

        // Mobile pattern: collapse to summary card once the user picks.
        setSenderExpanded(false);

        // Clear validation errors
        form.clearErrors(["senderName", "shipFrom"]);
      } else {
        // Update recipient fields
        form.setValue("recipientName", contact.contactName);
        form.setValue("recipientEmail", contact.contactEmail ?? "");
        form.setValue("recipientPhone", contact.contactPhone ?? "");

        // Update shipTo address
        form.setValue("shipTo.street1", contact.addressLine1);
        form.setValue("shipTo.street2", contact.addressLine2 ?? "");
        form.setValue("shipTo.city", contact.addressCity);
        form.setValue("shipTo.state", contact.addressProvince);
        form.setValue("shipTo.postalCode", contact.addressPostalCode ?? "");
        form.setValue(
          "shipTo.country",
          countryCodeForValue(contact.addressCountry) ?? ""
        );
        form.setValue("shipTo.latitude", contact.location.lat);
        form.setValue("shipTo.longitude", contact.location.lng);
        form.setValue("deliveryNotes", contact.deliveryInstructions ?? "");

        setShipToAddressBookEntryId(contact.id);

        // Mobile pattern: collapse to summary card once the user picks.
        setRecipientExpanded(false);

        // Clear validation errors
        form.clearErrors(["recipientName", "shipTo"]);
      }
    },
    [form]
  );

  return (
    <Form {...form}>
      <form id={formId} onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Sender Section */}
          <section
            ref={senderSection}
            tabIndex={-1}
            aria-label={t("orderDetails.sender")}
            className="rounded-large bg-content1 ring-1 ring-inset ring-divider p-4 sm:p-7"
          >
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <Chip
                  color="primary"
                  variant="flat"
                  startContent={<User className="h-4 w-4 ml-1" />}
                >
                  {t("orderDetails.sender")}
                </Chip>
                {/* Hide pill toggle on mobile when collapsed to summary — the
                    "Change →" button below serves the same purpose, and the
                    toggle overflows at 390px. */}
                <div
                  className={
                    !senderExpanded && form.watch("shipFrom.street1")
                      ? "hidden md:block"
                      : ""
                  }
                >
                  <PillToggle
                    options={
                      [
                        t("createShipment.addressBook"),
                        t("createShipment.manual"),
                      ] as const
                    }
                    value={
                      useSenderAddressBook
                        ? t("createShipment.addressBook")
                        : t("createShipment.manual")
                    }
                    onChange={(v) => {
                      const next = v === t("createShipment.addressBook");
                      setUseSenderAddressBook(next);
                      if (!next) setShipFromAddressBookEntryId(undefined);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Mobile compact summary — section 5 · Mobile · Details pattern. */}
            {!senderExpanded && form.watch("shipFrom.street1") && (
              <div className="md:hidden">
                <div className="text-base font-semibold text-default-900">
                  {form.watch("senderName") || t("orderDetails.sender")}
                </div>
                <div className="text-sm text-default-600 mt-1">
                  {form.watch("shipFrom.street1")},{" "}
                  {form.watch("shipFrom.city")}, {form.watch("shipFrom.state")}
                </div>
                <button
                  type="button"
                  onClick={() => setSenderExpanded(true)}
                  className="mt-3 text-sm font-semibold text-primary hover:underline"
                >
                  {t("createShipment.change")} →
                </button>
              </div>
            )}

            <div
              className={`space-y-4 ${
                !senderExpanded && form.watch("shipFrom.street1")
                  ? "hidden md:block"
                  : ""
              }`}
            >
              {useSenderAddressBook ? (
                <>
                  <AddressBookSelector
                    onSelect={(contact) =>
                      handleAddressBookSelect("sender", contact)
                    }
                    selectedContactId={shipFromAddressBookEntryId}
                    label={t("createShipment.selectFromAddressBook")}
                  />
                  <AddressFieldErrors errors={form.formState.errors.shipFrom} />
                  {(["senderPhone", "senderEmail"] as const).map((field) =>
                    form.formState.errors[field]?.message ? (
                      <p
                        key={field}
                        role="alert"
                        className="text-sm text-danger"
                      >
                        {form.formState.errors[field]?.message}
                      </p>
                    ) : null
                  )}
                  {form.formState.errors.senderName && (
                    <p className="text-sm font-medium text-destructive">
                      {form.formState.errors.senderName?.message}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <FormField
                    control={form.control}
                    name="senderName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("createShipment.name")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="senderEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("createShipment.emailOptional")}
                        </FormLabel>
                        <FormControl>
                          <Input type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="senderPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{`${t("bulkShip.phone")} *`}</FormLabel>
                        <FormControl>
                          <Input type="tel" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div>
                    <FormLabel>{t("createShipment.pickupAddress")}</FormLabel>
                    <AutocompleteAddress
                      onSelectedAddress={(address) => {
                        if (address) {
                          form.setValue(
                            "shipFrom.street1",
                            address.addressComponents.line1
                          );
                          form.setValue(
                            "shipFrom.street2",
                            address.addressComponents.line2 || ""
                          );
                          form.setValue(
                            "shipFrom.city",
                            address.addressComponents.city
                          );
                          form.setValue(
                            "shipFrom.state",
                            address.addressComponents.state
                          );
                          form.setValue(
                            "shipFrom.postalCode",
                            address.addressComponents.postalCode
                          );
                          form.setValue(
                            "shipFrom.country",
                            address.addressComponents.country
                          );
                          form.setValue(
                            "shipFrom.latitude",
                            address.location.lat
                          );
                          form.setValue(
                            "shipFrom.longitude",
                            address.location.lng
                          );
                        }
                      }}
                      placeholder={t("createShipment.searchPickupAddress")}
                      initialValue={form.watch("shipFrom.street1")}
                    />
                    {editParty === "sender" && (
                      <FormDescription>
                        {t("createShipment.addressCorrectionHelp")}
                      </FormDescription>
                    )}
                    <AddressFieldErrors
                      errors={form.formState.errors.shipFrom}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="pickupNotes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("createShipment.pickupNotes")}</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder={t(
                              "createShipment.pickupNotesPlaceholder"
                            )}
                          />
                        </FormControl>
                        <FormDescription>
                          {t("createShipment.localDeliveryOnly")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </div>
          </section>

          {/* Recipient Section */}
          <section
            ref={recipientSection}
            tabIndex={-1}
            aria-label={t("orderDetails.recipient")}
            className="rounded-large bg-content1 ring-1 ring-inset ring-divider p-4 sm:p-7"
          >
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <Chip
                  color="secondary"
                  variant="flat"
                  startContent={<MapPin className="h-4 w-4 ml-1" />}
                >
                  {t("orderDetails.recipient")}
                </Chip>
                <div
                  className={
                    !recipientExpanded && form.watch("shipTo.street1")
                      ? "hidden md:block"
                      : ""
                  }
                >
                  <PillToggle
                    options={
                      [
                        t("createShipment.addressBook"),
                        t("createShipment.manual"),
                      ] as const
                    }
                    value={
                      useRecipientAddressBook
                        ? t("createShipment.addressBook")
                        : t("createShipment.manual")
                    }
                    onChange={(v) => {
                      const next = v === t("createShipment.addressBook");
                      setUseRecipientAddressBook(next);
                      if (!next) setShipToAddressBookEntryId(undefined);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Mobile compact summary */}
            {!recipientExpanded && form.watch("shipTo.street1") && (
              <div className="md:hidden">
                <div className="text-base font-semibold text-default-900">
                  {form.watch("recipientName") || t("orderDetails.recipient")}
                </div>
                <div className="text-sm text-default-600 mt-1">
                  {form.watch("shipTo.street1")}, {form.watch("shipTo.city")},{" "}
                  {form.watch("shipTo.state")}
                </div>
                <button
                  type="button"
                  onClick={() => setRecipientExpanded(true)}
                  className="mt-3 text-sm font-semibold text-primary hover:underline"
                >
                  {t("createShipment.change")} →
                </button>
              </div>
            )}

            <div
              className={`space-y-4 ${
                !recipientExpanded && form.watch("shipTo.street1")
                  ? "hidden md:block"
                  : ""
              }`}
            >
              {useRecipientAddressBook ? (
                <>
                  <AddressBookSelector
                    onSelect={(contact) =>
                      handleAddressBookSelect("recipient", contact)
                    }
                    selectedContactId={shipToAddressBookEntryId}
                    label={t("createShipment.selectFromAddressBook")}
                    showDeliveryInstructions={false}
                  />
                  <AddressFieldErrors errors={form.formState.errors.shipTo} />
                  {(["recipientPhone", "recipientEmail"] as const).map(
                    (field) =>
                      form.formState.errors[field]?.message ? (
                        <p
                          key={field}
                          role="alert"
                          className="text-sm text-danger"
                        >
                          {form.formState.errors[field]?.message}
                        </p>
                      ) : null
                  )}
                  {form.formState.errors.recipientName && (
                    <p className="text-sm font-medium text-destructive">
                      {form.formState.errors.recipientName?.message}
                    </p>
                  )}
                  <FormField
                    control={form.control}
                    name="deliveryNotes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("createShipment.deliveryNotes")}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder={t(
                              "createShipment.deliveryNotesExample"
                            )}
                          />
                        </FormControl>
                        <FormDescription>
                          {t("createShipment.localDeliveryOnly")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              ) : (
                <>
                  <FormField
                    control={form.control}
                    name="recipientName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("createShipment.name")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="recipientEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("createShipment.emailOptional")}
                        </FormLabel>
                        <FormControl>
                          <Input type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="recipientPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{`${t("bulkShip.phone")} *`}</FormLabel>
                        <FormControl>
                          <Input type="tel" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div>
                    <FormLabel>{t("createShipment.deliveryAddress")}</FormLabel>
                    <AutocompleteAddress
                      onSelectedAddress={(address) => {
                        if (address) {
                          form.setValue(
                            "shipTo.street1",
                            address.addressComponents.line1
                          );
                          form.setValue(
                            "shipTo.street2",
                            address.addressComponents.line2 || ""
                          );
                          form.setValue(
                            "shipTo.city",
                            address.addressComponents.city
                          );
                          form.setValue(
                            "shipTo.state",
                            address.addressComponents.state
                          );
                          form.setValue(
                            "shipTo.postalCode",
                            address.addressComponents.postalCode
                          );
                          form.setValue(
                            "shipTo.country",
                            address.addressComponents.country
                          );
                          form.setValue(
                            "shipTo.latitude",
                            address.location.lat
                          );
                          form.setValue(
                            "shipTo.longitude",
                            address.location.lng
                          );
                        }
                      }}
                      placeholder={t("createShipment.searchDeliveryAddress")}
                      initialValue={form.watch("shipTo.street1")}
                    />
                    {editParty === "recipient" && (
                      <FormDescription>
                        {t("createShipment.addressCorrectionHelp")}
                      </FormDescription>
                    )}
                    <AddressFieldErrors errors={form.formState.errors.shipTo} />
                  </div>

                  <FormField
                    control={form.control}
                    name="deliveryNotes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("createShipment.deliveryNotes")}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder={t(
                              "createShipment.deliveryNotesPlaceholder"
                            )}
                          />
                        </FormControl>
                        <FormDescription>
                          {t("createShipment.localDeliveryOnly")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </div>
          </section>
        </div>

        {/* Packages Section */}
        <div className="rounded-large bg-content1 ring-1 ring-inset ring-divider p-4 sm:p-7">
          <div className="mb-4">
            <Chip
              variant="flat"
              startContent={<Package className="h-4 w-4 ml-1" />}
            >
              {t("createShipment.packages")}
            </Chip>
          </div>
          <div>
            <div className="space-y-4">
              {packageFields.map((field, index) => (
                <div key={field.id} className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">
                      {t("createShipment.package", { number: index + 1 })}
                    </h4>
                    <div className="flex items-center gap-2">
                      <PackagingSelector
                        selectedId={selectedPackagingIds[index]}
                        onSelect={(config) => {
                          if (config) {
                            setSelectedPackagingIds((prev) => ({
                              ...prev,
                              [index]: config.id,
                            }));
                            form.setValue(
                              `packages.${index}.length`,
                              config.length
                            );
                            form.setValue(
                              `packages.${index}.width`,
                              config.width
                            );
                            form.setValue(
                              `packages.${index}.height`,
                              config.height
                            );
                            form.setValue(
                              `packages.${index}.dimensionUnit`,
                              config.dimensionUnit
                            );
                          } else {
                            setSelectedPackagingIds((prev) => ({
                              ...prev,
                              [index]: null,
                            }));
                          }
                        }}
                      />
                      {packageFields.length > 1 && (
                        <Button
                          type="button"
                          variant="light"
                          size="sm"
                          isIconOnly
                          onPress={() => removePackage(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Package type */}
                  <FormField
                    control={form.control}
                    name={`packages.${index}.packageKind`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("createShipment.packageKind")}</FormLabel>
                        <Select
                          value={field.value ?? "PARCEL"}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger onBlur={field.onBlur}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="PARCEL">
                              {t("createShipment.parcel")}
                            </SelectItem>
                            <SelectItem value="THICK_ENVELOPE">
                              {t("createShipment.thickEnvelope")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  {/* Weight */}
                  <div className="grid grid-cols-2 gap-2">
                    <FormField
                      control={form.control}
                      name={`packages.${index}.weight`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("createShipment.weight")}{" "}
                            <span className="text-danger">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.1"
                              name={field.name}
                              onBlur={field.onBlur}
                              value={
                                Number.isFinite(field.value)
                                  ? String(field.value)
                                  : ""
                              }
                              onChange={(e) =>
                                field.onChange(parseFloat(e.target.value))
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`packages.${index}.weightUnit`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("packagingDialog.unit")}</FormLabel>
                          <FormControl>
                            <UnitSelector
                              type="weight"
                              value={field.value ?? "kg"}
                              onValueChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Dimensions */}
                  <div className="space-y-2">
                    <FormLabel>
                      {t("createShipment.dimensions")}{" "}
                      <span className="text-danger">*</span>
                    </FormLabel>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      <FormField
                        control={form.control}
                        name={`packages.${index}.length`}
                        render={({ field }) => (
                          <FormControl>
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="L"
                              name={field.name}
                              onBlur={field.onBlur}
                              value={
                                Number.isFinite(field.value)
                                  ? String(field.value)
                                  : ""
                              }
                              onChange={(e) =>
                                field.onChange(parseFloat(e.target.value))
                              }
                            />
                          </FormControl>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`packages.${index}.width`}
                        render={({ field }) => (
                          <FormControl>
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="W"
                              name={field.name}
                              onBlur={field.onBlur}
                              value={
                                Number.isFinite(field.value)
                                  ? String(field.value)
                                  : ""
                              }
                              onChange={(e) =>
                                field.onChange(parseFloat(e.target.value))
                              }
                            />
                          </FormControl>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`packages.${index}.height`}
                        render={({ field }) => (
                          <FormControl>
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="H"
                              name={field.name}
                              onBlur={field.onBlur}
                              value={
                                Number.isFinite(field.value)
                                  ? String(field.value)
                                  : ""
                              }
                              onChange={(e) =>
                                field.onChange(parseFloat(e.target.value))
                              }
                            />
                          </FormControl>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`packages.${index}.dimensionUnit`}
                        render={({ field }) => (
                          <FormControl>
                            <UnitSelector
                              type="dimension"
                              value={field.value ?? "cm"}
                              onValueChange={field.onChange}
                            />
                          </FormControl>
                        )}
                      />
                    </div>
                  </div>

                  {/* Optional Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name={`packages.${index}.isFragile`}
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                          <FormControl>
                            <Switch
                              isSelected={field.value}
                              onValueChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="mt-0!">
                            {t("createShipment.fragile")}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`packages.${index}.requiresCooling`}
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                          <FormControl>
                            <Switch
                              isSelected={field.value}
                              onValueChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="mt-0!">
                            {t("createShipment.requiresCooling")}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name={`packages.${index}.description`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("createShipment.descriptionOptional")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={t("createShipment.packageContents")}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              ))}

              <Button
                type="button"
                variant="bordered"
                onPress={() =>
                  appendPackage({
                    weight: undefined as unknown as number,
                    weightUnit: "kg",
                    length: undefined as unknown as number,
                    width: undefined as unknown as number,
                    height: undefined as unknown as number,
                    dimensionUnit: "cm",
                    packageKind: "PARCEL",
                    description: "",
                    isFragile: false,
                    requiresCooling: false,
                    declaredValueCents: 0,
                  })
                }
                startContent={<Plus className="h-4 w-4" />}
                className="w-full"
              >
                {t("createShipment.addPackage")}
              </Button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        {!hideNavigation && (
          <div className="flex justify-end">
            <Button type="submit" size="lg" color="primary">
              {t("createShipment.continueToPricing")}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}
